/**
 * @fileoverview Generic illustrated full-screen help panel shared by all feature areas.
 * @module ui-admin/components/HelpPanel
 *
 * Responsibilities: - Render a full-screen modal overlay with a sticky table of contents (left)
 *                     and a scrollable content area (right) for an arbitrary set of help
 *                     sections supplied via props.
 *                   - Smooth-scroll to a section when its ToC entry is clicked and highlight
 *                     the active entry; open at a requested section (deep link).
 *                   - Offer a "Take the tour →" action and close on × / Escape / backdrop.
 * Dependencies:     - React, lucide-react; section data + illustration registry via props.
 * Architecture:     - Presentation-only. All content arrives as static data bundled with the
 *                     app — no API, no Dexie, no mock data. Local-first by construction.
 *                     Single panel implementation (DRY) — feature areas (document builder,
 *                     inspection report management, …) pass their own sections/illustrations.
 * Notes:            - Section bodies are rendered from `HelpBlock[]`; never authored as JSX here.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HelpCircle, X, ArrowRight, Lightbulb, AlertTriangle } from 'lucide-react';
import type { GenericHelpSection, HelpBlock } from '../../help/types';
import { useTheme, useThemeCSS } from '../../contexts/ThemeContext';

/**
 * @contract HelpPanelProps
 * Purpose: Control the generic illustrated help panel.
 * Fields:
 * - open: whether the panel is shown.
 * - onClose: called on ×, Escape, or backdrop click.
 * - onStartTour: optional; called by "Take the tour →" (closes the panel and starts the tour).
 * - initialSectionId: optional section to open at (deep link).
 * - sections: ordered help sections to render.
 * - illustrations: registry mapping illustration ids to SVG React components.
 * - tocTitle: heading shown above the table of contents (e.g. "Builder Help").
 * - heading: content-area heading (e.g. "How the document builder works").
 * - ariaLabel: dialog aria-label.
 * - panelTestId: data-testid on the backdrop (defaults to "help-panel").
 */
export interface HelpPanelProps<Id extends string = string, Ill extends string = string> {
  open: boolean;
  onClose: () => void;
  onStartTour?: () => void;
  initialSectionId?: Id;
  sections: ReadonlyArray<GenericHelpSection<Id, Ill>>;
  illustrations: Partial<Record<Ill, React.FC>>;
  tocTitle: string;
  heading: string;
  ariaLabel: string;
  panelTestId?: string;
}

/** Render a single help block. */
function HelpBlockView({ block }: { block: HelpBlock }) {
  switch (block.type) {
    case 'paragraph':
      return <p className="mb-3 text-sm leading-relaxed text-gray-700">{block.text}</p>;
    case 'steps':
      return (
        <ol className="mb-3 ml-5 list-decimal space-y-1 text-sm text-gray-700">
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      );
    case 'tip':
      return (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-2 text-sm text-green-800">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{block.text}</span>
        </div>
      );
    case 'warning':
      return (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{block.text}</span>
        </div>
      );
    case 'keyShortcut':
      return (
        <div className="mb-2 flex items-center gap-3 text-sm text-gray-700">
          <kbd className="rounded-sm border border-gray-300 bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-800">{block.keys}</kbd>
          <span>{block.description}</span>
        </div>
      );
  }
}

/** Render one help section with its optional illustration. */
function HelpSectionView<Id extends string, Ill extends string>({
  section,
  illustrations,
  refCb,
}: {
  section: GenericHelpSection<Id, Ill>;
  illustrations: Partial<Record<Ill, React.FC>>;
  refCb: (el: HTMLElement | null) => void;
}) {
  const Illustration: React.FC | undefined = section.illustration ? illustrations[section.illustration] : undefined;
  return (
    <section
      ref={refCb}
      id={`help-section-${section.id}`}
      data-testid={`help-section-${section.id}`}
      className="mb-8 scroll-mt-4"
    >
      <h2 className="mb-1 text-lg font-bold text-gray-900">{section.title}</h2>
      <p className="mb-3 text-sm text-gray-500">{section.summary}</p>
      {Illustration && (
        <div className="mb-4">
          <Illustration />
        </div>
      )}
      {section.body.map((block, i) => <HelpBlockView key={i} block={block} />)}
    </section>
  );
}

/**
 * @component HelpPanel
 * Purpose:     Generic full-screen illustrated help system shared by feature areas.
 * Props:       HelpPanelProps.
 * Behavior:    ToC navigation smooth-scrolls to sections; Escape/×/backdrop close; "Take the
 *              tour →" calls onStartTour. Opens at `initialSectionId` when provided.
 * Data Source: static help content via props (no API/Dexie).
 * Constraints: no mock data; no network.
 */
export function HelpPanel<Id extends string = string, Ill extends string = string>({
  open,
  onClose,
  onStartTour,
  initialSectionId,
  sections,
  illustrations,
  tocTitle,
  heading,
  ariaLabel,
  panelTestId = 'help-panel',
}: HelpPanelProps<Id, Ill>) {
  const { theme } = useTheme();
  const themeCssString = useThemeCSS();
  const defaultId = sections[0]?.id;
  const [activeId, setActiveId] = useState<Id | undefined>(initialSectionId ?? defaultId);
  const sectionRefs = useRef<Partial<Record<Id, HTMLElement | null>>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  const scrollToSection = useCallback((id: Id) => {
    setActiveId(id);
    const el = sectionRefs.current[id];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // When opened (or asked to open at a specific section), jump there.
  useEffect(() => {
    if (!open) return;
    const target = initialSectionId ?? defaultId;
    setActiveId(target);
    if (!target) return;
    const el = sectionRefs.current[target];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
  }, [open, initialSectionId, defaultId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Scroll-spy: keep the ToC highlight in sync while the user scrolls.
  // A section becomes active when its top edge crosses the upper band of
  // the scrollport (rootMargin trims the bottom 2/3 so the last visible
  // section wins instead of whatever merely overlaps).
  useEffect(() => {
    if (!open) return;
    const root = contentRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const id = entry.target.id.replace(/^help-section-/, '') as Id;
          setActiveId(id);
        }
      },
      { root, rootMargin: '0px 0px -66% 0px' },
    );
    for (const s of sections) {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [open, sections]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid={panelTestId}
      onClick={onClose}
    >
      <style>{themeCssString}</style>
      <div
        role="dialog"
        aria-label={ariaLabel}
        className="theme-grid flex h-[88vh] w-full max-w-5xl overflow-hidden rounded-xl bg-white shadow-2xl"
        style={{ background: theme.cardBg, borderColor: theme.cardBorder }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Table of contents */}
        <nav className="hidden w-60 shrink-0 flex-col border-r border-gray-200 bg-gray-50 md:flex" aria-label="Help contents">
          <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
            <HelpCircle className="h-5 w-5 text-orange-500" />
            <span className="text-sm font-bold text-gray-800">{tocTitle}</span>
          </div>
          <ul className="flex-1 overflow-y-auto p-2">
            {sections.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  data-testid={`help-toc-${s.id}`}
                  aria-current={activeId === s.id}
                  onClick={() => scrollToSection(s.id)}
                  className={`w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors ${
                    activeId === s.id ? 'bg-orange-100 font-semibold text-orange-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
          {onStartTour && (
            <div className="border-t border-gray-200 p-3">
              <button
                type="button"
                data-testid="help-take-tour"
                onClick={() => { onClose(); onStartTour(); }}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
              >
                Take the tour <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </nav>

        {/* Content */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h1 className="text-base font-bold text-gray-900">{heading}</h1>
            <button type="button" aria-label="Close help" onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-5">
            {sections.map((s) => (
              <HelpSectionView
                key={s.id}
                section={s}
                illustrations={illustrations}
                refCb={(el) => { sectionRefs.current[s.id] = el; }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HelpPanel;
